import { createContext, forwardRef, useCallback, useContext, useLayoutEffect, useState, type HTMLAttributes, type ReactNode, type Ref } from 'react';
import { createPortal } from 'react-dom';

/**
 * Where the app's dialogs sit (owner 2026-10-21 23:46). A dialog (`.dialog-backdrop` > the box, styles.css) opened inside a
 * `DialogHost` — the event card beside the calendar, or its "Pełny widok" — is centred on the part of that host on the
 * screen and sized to it (minus a margin, scrolling inside when taller), instead of on the whole window where it was cut off.
 * The backdrop still covers the whole window, so the dialog stays modal. Outside a host, or with the host disabled
 * (a phone: dialogs over the whole screen), nothing changes.
 *
 * How: the host keeps its visible area as CSS variables (`--dlg-top/right/bottom/left`, insets from the window's edges)
 * and the `data-dialog-host` mark; styles.css turns them into the backdrop's padding. Dialogs rendered in place inherit
 * them without knowing; a dialog that has to leave its DOM parent (a form) uses `DialogPortal`, which renders it into the
 * host from `DialogHostContext` (or the page).
 */

export type DialogInsets = { top: number; right: number; bottom: number; left: number };
type Box = { left: number; top: number; right: number; bottom: number };

/** Smallest area worth centring a dialog on; less than that and the dialog uses the window. */
const MIN_WIDTH = 280;
const MIN_HEIGHT = 220;

/** The host's part on the screen, shrunk by `margin`, as insets from the window's edges; null when too small. */
export function dialogArea(host: Box, viewport: { width: number; height: number }, margin = 12): DialogInsets | null {
  const left = Math.max(host.left, 0);
  const top = Math.max(host.top, 0);
  const right = Math.min(host.right, viewport.width);
  const bottom = Math.min(host.bottom, viewport.height);
  if (right - left < MIN_WIDTH || bottom - top < MIN_HEIGHT) return null;
  const round = (n: number) => Math.round(n);
  return { top: round(top + margin), right: round(viewport.width - right + margin), bottom: round(viewport.height - bottom + margin), left: round(left + margin) };
}

/**
 * Owner 2026-10-23 20:57 („w dopożycz znowu wyskakuje małe okienko zanim pokaże całość, jeśli to jest możliwe do
 * zmieszczenia na ekranie”): a dialog that does not fit the card's part (it scrolls inside, or it is squeezed to the
 * area's full width) opens over the whole window instead. Once moved it stays there while open (no jumping).
 */
export function needsWindow(box: { scrollHeight: number; clientHeight: number; offsetWidth: number }, areaWidth: number): boolean {
  return box.scrollHeight > box.clientHeight + 1 || box.offsetWidth >= areaWidth - 1 || areaWidth < NARROW_AREA;
}
/** A card part narrower than this: dialogs open over the whole window at once (forms grow when their data arrive). */
const NARROW_AREA = 620;

function fitBackdrop(backdrop: HTMLElement) {
  if (backdrop.hasAttribute('data-dialog-full')) return;
  const box = backdrop.firstElementChild as HTMLElement | null;
  if (!box) return;
  const cs = getComputedStyle(backdrop);
  const areaWidth = backdrop.clientWidth - parseFloat(cs.paddingLeft || '0') - parseFloat(cs.paddingRight || '0');
  if (needsWindow(box, areaWidth)) backdrop.setAttribute('data-dialog-full', '');
}

/** The element dialogs belong to: a panel, or null for the page. */
export const DialogHostContext = createContext<HTMLElement | null>(null);

const SIDES = ['top', 'right', 'bottom', 'left'] as const;

function clear(el: HTMLElement) {
  el.removeAttribute('data-dialog-host');
  for (const side of SIDES) el.style.removeProperty(`--dlg-${side}`);
}

function place(el: HTMLElement) {
  const area = dialogArea(el.getBoundingClientRect(), { width: window.innerWidth, height: window.innerHeight });
  if (!area) return clear(el);
  el.setAttribute('data-dialog-host', '');
  for (const side of SIDES) {
    const value = `${area[side]}px`;
    if (el.style.getPropertyValue(`--dlg-${side}`) !== value) el.style.setProperty(`--dlg-${side}`, value);
  }
}

type HostProps = HTMLAttributes<HTMLElement> & { as?: 'div' | 'aside' | 'section'; enabled?: boolean; children?: ReactNode; 'data-testid'?: string };

/** An element whose dialogs open on it (see the file's comment). `enabled={false}`: a plain element. */
export const DialogHost = forwardRef<HTMLElement, HostProps>(function DialogHost({ as: Tag = 'div', enabled = true, children, ...rest }, outer) {
  const [el, setEl] = useState<HTMLElement | null>(null);
  const ref = useCallback(
    (node: HTMLElement | null) => {
      setEl(node);
      if (typeof outer === 'function') outer(node);
      else if (outer) outer.current = node;
    },
    [outer],
  );

  useLayoutEffect(() => {
    if (!el) return;
    if (!enabled) return clear(el);
    place(el);
    // the panel moves with the page's scroll, the window's size and the splitter; one measure per frame at most
    let frame = 0;
    const later = () => {
      if (!frame) frame = requestAnimationFrame(() => ((frame = 0), place(el)));
    };
    window.addEventListener('resize', later);
    document.addEventListener('scroll', later, { capture: true, passive: true });
    const resized = typeof ResizeObserver === 'function' ? new ResizeObserver(later) : null;
    resized?.observe(el);
    // a dialog that does not fit the card's part goes over the whole window (20:57). Decided once, when it opens and
    // before it is painted (a MutationObserver runs before the next frame): owner 21:11 „dziwnie przeskakuje” — it never
    // moves later, e.g. when its data arrive
    const watch = () => {
      if (!el.hasAttribute('data-dialog-host')) return;
      el.querySelectorAll<HTMLElement>('.dialog-backdrop').forEach((b) => {
        if (b.dataset.dlgPlaced) return;
        b.dataset.dlgPlaced = '1';
        fitBackdrop(b);
      });
    };
    const added = typeof MutationObserver === 'function' ? new MutationObserver(watch) : null;
    added?.observe(el, { childList: true, subtree: true });
    watch();
    return () => {
      window.removeEventListener('resize', later);
      document.removeEventListener('scroll', later, { capture: true });
      resized?.disconnect();
      added?.disconnect();
      if (frame) cancelAnimationFrame(frame);
      clear(el);
    };
  }, [el, enabled]);

  return (
    <DialogHostContext.Provider value={enabled ? el : null}>
      <Tag ref={ref as Ref<never>} {...rest}>
        {children}
      </Tag>
    </DialogHostContext.Provider>
  );
});

/**
 * Renders a dialog outside its DOM parent (e.g. out of a form) but still in its host: the panel, or the page.
 * `fullWindow`: a tool that fills the window (the map), not bound to the card's part on the screen (owner 2026-10-22
 * 14:44: as much as possible in the window).
 */
export function DialogPortal({ children, fullWindow = false }: { children: ReactNode; fullWindow?: boolean }) {
  const host = useContext(DialogHostContext);
  return createPortal(children, fullWindow ? document.body : (host ?? document.body));
}
